import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { CancelConditionState } from "../objects/cancelConditionState";
import { CancelCondition } from "../objects/cancelCondition";
import { EMPTY_CANCEL_CONDITION_LIST_CONFIGURATION, EMPTY_CANCEL_CONDITION_LIST_CRITERIA } from "../constants/cancelConditionConstants";
import { CancelConditionListCriteria } from "../objects/cancelConditionList";
import { Preferences } from "../../profile/objects/preferences";
import { setCancelConditionPreference } from "../utils/setCancelConditionPreference";
import { Column } from "../objects/column";

const initialState: CancelConditionState = {
    cancel_condition_list: [],
    cancel_condition_list_count: 0,
    cancel_condition_list_criteria: EMPTY_CANCEL_CONDITION_LIST_CRITERIA,
    cancel_condition_list_configuration: EMPTY_CANCEL_CONDITION_LIST_CONFIGURATION,
    cancel_condition_list_loading: false,
    cancel_condition_list_refresh: false,
    first_preference_loaded: false,
    cancel_condition: null,
    cancel_condition_delete: null,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setCancelConditionListReducer = (state: CancelConditionState, action: PayloadAction<CancelCondition[]>): CancelConditionState => ({
    ...state, cancel_condition_list: action.payload
});
const setCancelConditionListCountReducer = (state: CancelConditionState, action: PayloadAction<number>): CancelConditionState => ({
    ...state, cancel_condition_list_count: action.payload
});

const setCancelConditionListLoadingReducer = (state: CancelConditionState, action: PayloadAction<boolean>): CancelConditionState => ({
    ...state, cancel_condition_list_loading: action.payload
});
const toggleCancelConditionRefreshListReducer = (state: CancelConditionState): CancelConditionState => ({
    ...state, cancel_condition_list_refresh: !state.cancel_condition_list_refresh
});
const setCancelConditionReducer = (state: CancelConditionState, action: PayloadAction<CancelCondition | null>): CancelConditionState => ({
    ...state, cancel_condition: action.payload
});
const setCancelConditionDeleteReducer = (state: CancelConditionState, action: PayloadAction<CancelCondition | null>): CancelConditionState => ({
    ...state, cancel_condition_delete: action.payload
});
const setCancelConditionListCriteriaReducer = (state: CancelConditionState, action: PayloadAction<CancelConditionListCriteria>): CancelConditionState => ({
    ...state, cancel_condition_list_criteria: action.payload
});

const setColumnsReducer = (state: CancelConditionState, action: PayloadAction<Column[]>): CancelConditionState => ({
    ...state, cancel_condition_list_configuration: { ...state.cancel_condition_list_configuration, columns: action.payload },
});

const toggleColumnReducer = (state: CancelConditionState, action: PayloadAction<number>): CancelConditionState => {
    const columns = cloneDeep([...state.cancel_condition_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, cancel_condition_list_configuration: { ...state.cancel_condition_list_configuration, columns } };
};

const addCancelConditionListConfigurationPreferenceReducer = (state: CancelConditionState, action: PayloadAction<Preferences[]>): CancelConditionState => ({
    ...state, cancel_condition_list_configuration: { ...state.cancel_condition_list_configuration, preferences: [...state.cancel_condition_list_configuration.preferences, ...action.payload] }
});

const setCancelConditionListConfigurationPreferenceReducer = (state: CancelConditionState, action: PayloadAction<Preferences[]>): CancelConditionState => ({
    ...state, cancel_condition_list_configuration: { ...state.cancel_condition_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CancelConditionState, action: PayloadAction<Preferences[]>): CancelConditionState => {
    const { preferences, columns, order_by, order } = setCancelConditionPreference(action.payload, state.cancel_condition_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        cancel_condition_list_configuration: { ...state.cancel_condition_list_configuration, preferences, columns },
        cancel_condition_list_criteria: { ...state.cancel_condition_list_criteria, order, order_by }
    };
};

export const cancelCondition = createSlice({
    name: 'cancel_condition',
    initialState,
    reducers: {
        setCancelConditionList: setCancelConditionListReducer,
        setCancelConditionListCount: setCancelConditionListCountReducer,
        setCancelConditionListLoading: setCancelConditionListLoadingReducer,
        toggleCancelConditionRefreshList: toggleCancelConditionRefreshListReducer,
        setCancelCondition: setCancelConditionReducer,
        setCancelConditionDelete: setCancelConditionDeleteReducer,
        setCancelConditionListCriteria: setCancelConditionListCriteriaReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        addCancelConditionListConfigurationPreference: addCancelConditionListConfigurationPreferenceReducer,
        setCancelConditionListConfigurationPreference: setCancelConditionListConfigurationPreferenceReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setCancelConditionList,
    setCancelConditionListCount,
    setCancelConditionListLoading,
    toggleCancelConditionRefreshList,
    setCancelCondition,
    setCancelConditionDelete,
    setCancelConditionListCriteria,
    setColumns,
    toggleColumn,
    addCancelConditionListConfigurationPreference,
    setCancelConditionListConfigurationPreference
} = cancelCondition.actions;
